import { GameMode } from '@/types/api.generated';
import { logger } from '@/utils/logging';
import { runStoreUpdate } from '@/utils/zustand';

import { PicksValidationProcessor, ValidateResponse } from '.';
import { PromoRemoved } from '../components/PromoRemovedModalProvider';
import { useBetslipStore } from '../hooks/use-betslip-store';
import { computeEntryPromotions } from '../hooks/use-fantasy-entry-promotions';
import { BetslipPick, PlayerWithTeam } from '../types';

export type PromotionsProcessorContext = {
    modalContext: PromoRemoved;
    removeFunction: (picks: { eventId: string; player: PlayerWithTeam }[]) => void;
    reValidatePicks: () => void;
    latestPick: BetslipPick | undefined;
};

export class PromotionsProcessor extends PicksValidationProcessor<PromotionsProcessorContext> {
    process(data: ValidateResponse): void {
        const betslip = useBetslipStore.getState().betslip;
        const activeGameMode = useBetslipStore.getState().activeGameMode;
        const selectedPromo = useBetslipStore.getState().selectedPromo;
        const previouslySelectedPromo = useBetslipStore.getState().previouslySelectedPromo;

        const { setPreviouslySelectedPromo, setSelectedPromo, setAutoPromoApplied } =
            useBetslipStore.getState().actions;

        const activeGameModeValidationData = data.validateEntryOnEntryRules.find(it => it.gameMode === activeGameMode);
        const mysteryBetValidationData = data.validateEntryOnEntryRules.find(it => it.gameMode === GameMode.Mystery);

        const entryPromotions = computeEntryPromotions({
            activeGameMode,
            betslip,
            activeGameModePromotions: activeGameModeValidationData?.promotions ?? [],
            mysteryBetValidationData: mysteryBetValidationData,
        });

        if (selectedPromo) {
            const hasMysteryPromoApplied = selectedPromo.promotion.type === GameMode.Mystery;
            const hasRegularPromoApplied = !hasMysteryPromoApplied;

            const selectedPromoWithStatus = entryPromotions.find(p => p.promotion.id === selectedPromo.promotion.id);
            if (!selectedPromoWithStatus) {
                //When the promotion is no longer available (due to game mode changes)
                //we decided to instantly remove it without confirmation from the user
                logger.debug('Selected promotion is not available, will be removed :', selectedPromo);
                setPreviouslySelectedPromo(selectedPromo);
                setSelectedPromo(undefined);
                this.context.reValidatePicks();
                return;
            } else if (selectedPromoWithStatus?.errors?.length) {
                //When the promotion has errors sent by the API, we will show those errors first to the user

                //first remove the promo to make sure promo is not applied when user closes the app in this point
                logger.debug('Selected promotion has errors, will be removed:', selectedPromoWithStatus);
                setSelectedPromo(undefined);
                if (hasRegularPromoApplied) {
                    this.context.modalContext.showPromoRemovedModal({
                        promoError: selectedPromoWithStatus.errors[0],
                        removePromo: () => {
                            logger.debug('Selected promotion has an error, will be removed :', selectedPromoWithStatus);
                            //User decided to remove the 'promo', we must revalidate picks
                            this.context.reValidatePicks();
                        },
                        removePlayers: players => {
                            //User decided to remove players to keep the promo, so we store it as previously selected
                            //We don't need revalidation here, as removing players (removeSelection) will trigger a new validation
                            setPreviouslySelectedPromo(selectedPromoWithStatus);
                            this.context.removeFunction(players);
                        },
                        latestPick: this.context.latestPick,
                    });
                    //Return, as we don't want to handle the next processor until the user takes the action
                    return;
                }
            } else {
                // Promotion is valid, update store to have latest data
                setSelectedPromo(selectedPromoWithStatus);
            }
            this.nextProcessor?.process(data);
            return;
        }

        if (previouslySelectedPromo) {
            //Try to re-apply previously selected promo if it became available

            // SCENARIO: apply promo on Perfect with PERFECT criteria
            // move to Dynamic -> promo will dissapear
            // go back to Perfect -> promo will still be applied.
            const promoWithStatus = entryPromotions.find(p => p.promotion.id === previouslySelectedPromo.promotion.id);
            const previousPromoIsValid =
                promoWithStatus && (promoWithStatus.errors === undefined || promoWithStatus.errors?.length === 0);
            const hadMysteryAppliedBefore = previouslySelectedPromo.promotion.type === GameMode.Mystery;

            if (previousPromoIsValid || hadMysteryAppliedBefore) {
                // previously selected promo is now available again
                logger.debug('Previously not available promotion is now available again :', promoWithStatus);
                setSelectedPromo(promoWithStatus);
                setPreviouslySelectedPromo(undefined);
                this.context.reValidatePicks();
                return;
            }
        }

        // Handle the auto-apply promo scenario

        // The promotion that should be auto-applied, if any
        const autoApplyPromo = entryPromotions?.find(p => (p.errors?.length ?? 0) === 0 && p.promotion.autoApply);
        const autoPromoApplied = useBetslipStore.getState().autoPromoApplied;

        // Indicates if an auto-apply promotion should be applied or not
        // False if a promotion is already selected or if the auto-promo has already been applied
        const shouldApplyPromotion = !selectedPromo && !!autoApplyPromo && !autoPromoApplied;

        if (shouldApplyPromotion) {
            runStoreUpdate(() => {
                setSelectedPromo(autoApplyPromo);
                setAutoPromoApplied(true);
            });
            this.context.reValidatePicks();
            return;
        }
        this.nextProcessor?.process(data);
    }
}
